/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { CARD_TYPES } from 'carbon-addons-iot-react';
import { defineMessages } from 'react-intl';
import moment from 'moment';

import {
  CUSTOM_CARD_TYPES,
  DASHBOARD_DATA_TYPES,
  MAX_PINNED_DASHBOARDS,
  TIME_GRAINS,
} from '../../Common/constants/constants';
import { TimeRangeOption, TimeRanges } from '../../Common/types/DateTypes';
import { Card, DashboardPinType, ImageType, Range, Threshold } from '../types/dashboardTypes';
import AlertsTableContainer from '../../Common/containers/AlertsTableContainer/AlertsTableContainer';
import ThirdPartyCardContainer from '../containers/ThirdPartyCardContainer/ThirdPartyCardContainer';

import { updateThresholdsInTimeSeriesCard } from './dashboardEditorUtils';
import { getAlertCardDefaultColumsState } from './dashboardUtils';

import { FormatMessageType } from 'CommonTypes';

const messages = defineMessages({
  last30Minutes: 'Last 30 minutes',
  lastHour: 'Last hour',
  last6Hours: 'Last 6 hours',
  last12Hours: 'Last 12 hours',
  last24Hours: 'Last 24 hours',
  last7Days: 'Last 7 days',
  lastMonth: 'Last month',
  lastQuarter: 'Last quarter',
  lastYear: 'Last year',
  last5Years: 'Last 5 years',
  default: 'Last {count} {interval}',
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
  weeks: 'weeks',
  months: 'months',
  quarters: 'quarters',
  years: 'years',
});

export const handleNewPin = (
  newPin: DashboardPinType,
  isSettingHomepage: boolean,
  dashboardPins: DashboardPinType[],
  setHomePage: Function,
  updateDashboardPin: Function,
  setShowSetHomePageToast: Function,
  name: string,
  setIsSettingHomepage: Function,
  setIsToastAddPinVisible: Function,
  hierarchyPath?: string
): boolean => {
  if (isSettingHomepage) {
    const { dashboardPinId } = newPin;
    const updatedDashboardPins = [...dashboardPins, newPin];
    setHomePage(dashboardPinId, updatedDashboardPins, updateDashboardPin, hierarchyPath).then(
      () => {
        setShowSetHomePageToast(name);
        setIsSettingHomepage(false);
        return true;
      }
    );
  } else {
    setIsToastAddPinVisible(true);
    return true;
  }
  return true;
};

export const captureAndPinDashboard = (
  pinThumbnailCount: number,
  addDashboardPin: Function,
  deviceId: string | null,
  name: string,
  dashboardMetaId: number,
  description: string,
  category: string,
  isSummaryDashboard: boolean,
  dimensionFilter: Record<string, string>,
  isSettingHomepage: boolean,
  dashboardPins: DashboardPinType[],
  updateDashboardPin: Function,
  setHomePage: Function,
  setShowSetHomePageToast: Function,
  setIsSettingHomepage: Function,
  setIsToastAddPinVisible: Function,
  notifyCapturingPinThumbnail: Function,
  captureDashboardToThumbnail: Function,
  hierarchyPath: string
): Promise<boolean> => {
  if (pinThumbnailCount >= MAX_PINNED_DASHBOARDS) {
    // NOTE: if we have more than 30 thumbnails already, skip the capture (until we
    // add pagination to the API layer)
    return addDashboardPin(
      hierarchyPath,
      name,
      dashboardMetaId,
      description,
      category,
      isSummaryDashboard,
      false, // favorite
      false, // homePage
      dimensionFilter,
      null, // thumbnail
      deviceId
    ).then(newPin =>
      handleNewPin(
        newPin,
        isSettingHomepage,
        dashboardPins,
        setHomePage,
        updateDashboardPin,
        setShowSetHomePageToast,
        name,
        setIsSettingHomepage,
        setIsToastAddPinVisible,
        hierarchyPath
      )
    );
  }

  notifyCapturingPinThumbnail();

  return captureDashboardToThumbnail()
    .then(thumbnailImage =>
      addDashboardPin(
        hierarchyPath,
        name,
        dashboardMetaId,
        description,
        category,
        isSummaryDashboard,
        false, // favorite
        false, // homePage
        dimensionFilter,
        thumbnailImage,
        deviceId
      ).then(newPin =>
        handleNewPin(
          newPin,
          isSettingHomepage,
          dashboardPins,
          setHomePage,
          updateDashboardPin,
          setShowSetHomePageToast,
          name,
          setIsSettingHomepage,
          setIsToastAddPinVisible,
          hierarchyPath
        )
      )
    )
    .catch(err => {
      console.error(err);
    });
};

export const updateImageInCard = (
  card: Card,
  images: ImageType[],
  entityTypeId?: string,
  entity?: string
): Card => {
  if (Array.isArray(images) && images.length > 0) {
    let matchingImageSrc = null;
    const filteredImages = images.filter(
      image => isNil(card?.content?.src) && card?.content?.id && card?.content?.id === image.id
    );
    if (filteredImages.length > 0) {
      if (filteredImages.length === 1) {
        matchingImageSrc = filteredImages[0].src;
      }
      // there are cases where we will have images at device type level and that is override by images in device level
      else if (!isNil(entityTypeId) || !isNil(entity)) {
        if (!isNil(entity) && filteredImages.length === 2) {
          // If device level image present use it or else use the device type level image
          const deviceImage: ImageType[] = filteredImages.filter(
            image => image.entity === entity && image.entityTypeId === entityTypeId
          );
          if (!isEmpty(deviceImage)) {
            matchingImageSrc = deviceImage[0].src;
          } else {
            // Find image at device type level
            const deviceTypeImage: ImageType[] = filteredImages.filter(
              image => image.entityTypeId === entityTypeId && !image.hasOwnProperty('entity')
            );
            matchingImageSrc = !isEmpty(deviceTypeImage) ? deviceTypeImage[0].src : null;
          }
        } else {
          // Find image at device type level
          const deviceTypeImage: ImageType[] = filteredImages.filter(
            image => image.entityTypeId === entityTypeId
          );
          matchingImageSrc = !isEmpty(deviceTypeImage) ? deviceTypeImage[0].src : null;
        }
      }
    }
    // return matchingImageSrc in the card
    return matchingImageSrc
      ? {
          ...card,
          content: { ...card.content, src: matchingImageSrc },
        }
      : card;
  }

  return card;
};

export const addRuntimeInformationToCard = (
  card: Card,
  // entityTypeUUID: string,
  entityTypeName: string,
  entityId: string,
  images: ImageType[],
  thresholds: Array<Threshold>,
  isHierarchyDashboard: boolean,
  formatMessage: FormatMessageType,
  hierarchyPath: string,
  hierarchyType: string
): Card => {
  let updatedCard = card;
  // We have to get a special extra get for IMAGE Card
  if (card.type === CARD_TYPES.IMAGE && card.content.src === undefined) {
    updatedCard = updateImageInCard(card, images, entityTypeName, entityId);
  }
  // If a timeseries card has markup data, we need to do a special get to determine the correct alert threshold
  if (
    card.type === CARD_TYPES.TIMESERIES &&
    card?.dataSource?.additionalData?.type === DASHBOARD_DATA_TYPES.ALERT &&
    !isEmpty(card?.dataSource?.additionalData?.dataFilter) &&
    isEmpty(card?.content?.thresholds)
  ) {
    updatedCard = updateThresholdsInTimeSeriesCard(card, thresholds);
  }

  if (card.type === CUSTOM_CARD_TYPES.ALERT) {
    updatedCard = {
      ...card,
      columnsState:
        card.columnsState || getAlertCardDefaultColumsState(formatMessage, isHierarchyDashboard),
      // This function is invoked in the Card component as its child. It's second argument is all of the props given to Card
      content: (
        size,
        {
          cardToolbar,
          title: cardTitle,
          description,
          data,
          onCardAction,
          id,
          isExpanded,
          columnsState,
          customRowHeight,
          customRowCount,
        } // Need to do this here because this is the only place with knowledge about the entityTypeId
      ): JSX.Element => (
        <AlertsTableContainer
          entityTypeId={entityTypeName}
          entityId={entityId}
          isHierarchyDashboard={isHierarchyDashboard}
          title={cardTitle}
          tooltip={description}
          customToolbarContent={cardToolbar}
          dashboardData={data}
          size={size}
          onCardAction={onCardAction}
          id={id}
          isExpanded={isExpanded}
          columnsState={columnsState}
          hasShortRows={customRowHeight === 'short'}
          numberOfRowsPerPage={customRowCount}
          testId={`alerts-table-card-content-${cardTitle}`}
          hierarchyPath={hierarchyPath}
        />
      ),
      type: CUSTOM_CARD_TYPES.ALERT,
      availableActions: { expand: true },
      hideHeader: true,
      hasShortRows: true,
    };
  }

  // REMOVE WHEN ISSUE IS COMPLETED: https://github.com/carbon-design-system/carbon-addons-iot-react/issues/3090#issue-1065517421
  if (card.type === CARD_TYPES.VALUE && !card.hasOwnProperty('fontSize')) {
    updatedCard.fontSize = 18;
  }

  if (card.type === CUSTOM_CARD_TYPES.THIRD_PARTY) {
    updatedCard = {
      ...card,
      // This function is invoked in the Card component as its child. It's second argument is all of the props given to Card
      content: (size, { title, data, id, size: cardSize, customContent }): JSX.Element => (
        <ThirdPartyCardContainer
          hierarchyPath={hierarchyPath}
          hierarchyType={hierarchyType}
          data={data}
          title={title}
          size={size}
          cardSize={cardSize}
          id={id}
          customContent={customContent}
        />
      ),
      availableActions: { expand: true, extra: true },
    };
  }

  return updatedCard;
};

/**
 *
 * @param {Moment} endDate , selected Dashboard End Date
 * @param {string} grain , optional parameter to use to calculate startdate
 */
export const determineDashboardStartDate = (
  endDate: moment.Moment,
  grain: string
): moment.Moment => {
  const startDate = endDate.clone();
  // Instance Dashboard defaults to 7 days
  if (!grain) {
    return startDate.subtract(7, 'days');
  }
  // Otherwise these are the ranges that should be used for each granularity because the dashboard is going wider!

  switch (grain) {
    case TIME_GRAINS.HOUR:
      startDate.subtract(24, 'hours');
      break;
    case TIME_GRAINS.DAY:
      startDate.subtract(7, 'days');
      break;
    case TIME_GRAINS.WEEK:
      startDate.subtract(4, 'weeks');
      break;
    case TIME_GRAINS.MONTH:
      startDate.subtract(12, 'months');
      break;
    case TIME_GRAINS.YEAR:
      startDate.subtract(5, 'years');
      break;
    default:
      break;
  }
  return startDate;
};

/**
 *
 * @param {string} grain , parameter to determine with preset to use
 */
export const determineDashboardPreset = (
  timeRangeOptions: TimeRangeOption[],
  grain: string
): TimeRangeOption => {
  switch (grain) {
    case TIME_GRAINS.HOUR:
      return timeRangeOptions.find(
        timeRangeOption => timeRangeOption.id === TimeRanges.LAST_24_HOURS
      );
    // Instance Dashboard defaults to 7 days
    case TIME_GRAINS.DAY:
    default:
      return timeRangeOptions.find(
        timeRangeOption => timeRangeOption.id === TimeRanges.LAST_7_DAYS
      );
    case TIME_GRAINS.WEEK:
      return timeRangeOptions.find(timeRangeOption => timeRangeOption.id === TimeRanges.LAST_MONTH);
    case TIME_GRAINS.MONTH:
      return timeRangeOptions.find(timeRangeOption => timeRangeOption.id === TimeRanges.LAST_YEAR);
    case TIME_GRAINS.YEAR:
      return timeRangeOptions.find(
        timeRangeOption => timeRangeOption.id === TimeRanges.LAST_5_YEARS
      );
  }
};

/**
 * Reusable function to determine the default presets
 * @param formatMessage
 * @returns
 */
export const determineDefaultPresets = (
  formatMessage: FormatMessageType,
  cardRange?: Range
): TimeRangeOption[] => [
  ...(cardRange
    ? [
        // default means that the card relative came from the JSON
        {
          id: TimeRanges.DEFAULT,
          label: formatMessage(messages.default, {
            // format both the singular and plural interval
            interval: formatMessage(
              messages[
                Math.abs(cardRange.count) === 1 ? cardRange.interval : `${cardRange.interval}s`
              ]
            ),
            count: Math.abs(cardRange.count),
          }),
          offset: moment().diff(
            // dynamically determine the offset
            moment().subtract(
              Math.abs(cardRange.count), // count is negative
              cardRange.interval as moment.unitOfTime.DurationConstructor
            ),
            'minutes'
          ), // TODO: update the offset here to use the correct range
        },
      ]
    : []),
  {
    id: TimeRanges.LAST_30_MINUTES,
    label: formatMessage(messages.last30Minutes),
    offset: 30,
  },
  {
    id: TimeRanges.LAST_HOUR,
    label: formatMessage(messages.lastHour),
    offset: 60,
  },
  {
    id: TimeRanges.LAST_6_HOURS,
    label: formatMessage(messages.last6Hours),
    offset: 60 * 6,
  },
  {
    id: TimeRanges.LAST_12_HOURS,
    label: formatMessage(messages.last12Hours),
    offset: 60 * 12,
  },
  {
    id: TimeRanges.LAST_24_HOURS,
    label: formatMessage(messages.last24Hours),
    offset: 60 * 24,
  },
  {
    id: TimeRanges.LAST_7_DAYS,
    label: formatMessage(messages.last7Days),
    offset: moment().diff(moment().subtract(7, 'days')) / 1000 / 60,
  },
  {
    id: TimeRanges.LAST_MONTH,
    label: formatMessage(messages.lastMonth),
    offset: moment().diff(moment().subtract(1, 'month')) / 1000 / 60,
  },
  {
    id: TimeRanges.LAST_QUARTER,
    label: formatMessage(messages.lastQuarter),
    offset: moment().diff(moment().subtract(1, 'quarter')) / 1000 / 60,
  },
  {
    id: TimeRanges.LAST_YEAR,
    label: formatMessage(messages.lastYear),
    offset: moment().diff(moment().subtract(1, 'year')) / 1000 / 60,
  },
  {
    id: TimeRanges.LAST_5_YEARS,
    label: formatMessage(messages.last5Years),
    offset: moment().diff(moment().subtract(5, 'year')) / 1000 / 60,
  },
];
